import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { subscriptionsApi } from '@api/endpoints/subscriptions'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import type { SubscriptionPlanDto } from '@api/types/subscription'

interface GrantCreditDialogProps {
  open: boolean
  onClose: () => void
  userId: string
  onSuccess: () => void
}

export default function GrantCreditDialog({ open, onClose, userId, onSuccess }: GrantCreditDialogProps) {
  const mobileDialogProps = useMobileDialogProps()
  const [plans, setPlans] = useState<SubscriptionPlanDto[]>([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [creditMonths, setCreditMonths] = useState<number>(1)
  const [reason, setReason] = useState('')
  const [expiresAt, setExpiresAt] = useState<Date | null>(null)
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!open) return
    setPlansLoading(true)
    subscriptionsApi.getPlans()
      .then(data => setPlans((data || []).filter(p => !p.isFreeTier)))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false))
  }, [open])

  const resetForm = () => {
    setSelectedPlanId('')
    setCreditMonths(1)
    setReason('')
    setExpiresAt(null)
    setNotes('')
    setError(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    if (!selectedPlanId || !reason.trim() || creditMonths < 1 || creditMonths > 24) return
    setSubmitting(true)
    setError(null)
    try {
      await subscriptionsApi.grantCredit({
        userId,
        subscriptionPlanId: selectedPlanId,
        creditMonths,
        reason: reason.trim(),
        expiresAt: expiresAt ? expiresAt.toISOString() : null,
        notes: notes.trim() || null,
      })
      resetForm()
      onSuccess()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant credit')
    } finally {
      setSubmitting(false)
    }
  }

  const isValid = selectedPlanId && reason.trim() && creditMonths >= 1 && creditMonths <= 24

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>Grant Subscription Credit</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <FormControl fullWidth required>
            <InputLabel>Subscription Plan</InputLabel>
            <Select
              value={selectedPlanId}
              label="Subscription Plan"
              onChange={(e) => setSelectedPlanId(e.target.value)}
              disabled={plansLoading}
            >
              {plansLoading ? (
                <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> Loading plans...</MenuItem>
              ) : (
                plans.map(plan => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.displayName} ({plan.formattedMonthlyPrice}/mo)
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Credit Months"
            type="number"
            required
            fullWidth
            value={creditMonths}
            onChange={(e) => setCreditMonths(Math.max(1, Math.min(24, parseInt(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 24 }}
            helperText="1-24 months"
          />

          <TextField
            label="Reason"
            required
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Contest winner, Support compensation"
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Expiration Date (optional)"
              value={expiresAt}
              onChange={setExpiresAt}
              disablePast
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>

          <TextField
            label="Notes (optional)"
            fullWidth
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid || submitting}>
          {submitting ? 'Granting...' : 'Grant Credit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
